import { Link, useLocation } from "react-router";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { to } from "@/site/parts";

/** An empty field of view: graticule, reticle, nothing in it. */
function EmptyField() {
  const W = 600;
  const H = 450;
  const lines: React.ReactNode[] = [];
  for (let u = 50; u < W; u += 50) lines.push(<line key={`v${u}`} x1={u} y1={0} x2={u} y2={H} />);
  for (let v = 50; v < H; v += 50) lines.push(<line key={`h${v}`} x1={0} y1={v} x2={W} y2={v} />);
  return (
    <div className="plate plate-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="An empty field of view: no spots">
        <rect width={W} height={H} fill="var(--panel)" />
        <g stroke="var(--grat)">{lines}</g>
        <g stroke="var(--ink-3)" strokeWidth={1.5} strokeDasharray="6 6">
          <line x1={W / 2} y1={0} x2={W / 2} y2={H} />
          <line x1={0} y1={H / 2} x2={W} y2={H / 2} />
        </g>
        <circle cx={W / 2} cy={H / 2} r={64} fill="none" stroke="var(--ink)" strokeWidth={1.5} />
        <circle cx={W / 2} cy={H / 2} r={6} fill="var(--signal)" />
        <text
          x={W - 18}
          y={H - 18}
          textAnchor="end"
          fontFamily="var(--mono)"
          fontSize={16}
          fill="var(--ink-3)"
        >
          0 SPOTS · 0 PAIRS
        </text>
      </svg>
    </div>
  );
}

const WAYS: [string, string, string][] = [
  [to.home, "Identify", "Upload a photograph and match it against the registry."],
  [to.registry, "Registry", "Every individual on record, searchable by name or number."],
  [to.about, "Method", "How the spots are read and matched, and how well."],
];

export function NotFound() {
  const { pathname } = useLocation();
  useDocumentMeta({ title: "Not found" });
  return (
    <section className="in-wrap in-grid" style={{ paddingTop: 56, rowGap: 40 }}>
      <div style={{ gridColumn: "span 6", display: "grid", gap: 24, alignContent: "start" }}>
        <p className="label label-signal">404 · Out of range</p>
        <h1 className="display-2">Nothing at these coordinates.</h1>
        <dl className="kv">
          <div>
            <dt>Requested</dt>
            <dd className="mono" style={{ wordBreak: "break-all" }}>
              {pathname}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>No page, specimen or certificate here</dd>
          </div>
        </dl>
        <ul className="ways" aria-label="Where to go instead">
          {WAYS.map(([href, name, text]) => (
            <li key={href}>
              <Link to={href}>
                <span className="h-3">{name}</span>
                <span className="muted">{text}</span>
                <span className="btn-arrow" aria-hidden="true">
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div style={{ gridColumn: "8 / span 5" }}>
        <div className="fig-head">
          <span className="label">Fig. A · Field of view</span>
          <span className="label">Empty</span>
        </div>
        <EmptyField />
      </div>
    </section>
  );
}
